/* Webapp half of the intercom `security` crypto
   (docs/imp-docs/intercom-bus/INTERCOM-BUS.md §4).

   RSA-OAEP against the `publicKey` from `security/generate.encryption.keys`, with the
   padding parameters PINNED EXPLICITLY: WebCrypto's `{ name: 'RSA-OAEP', hash:
   'SHA-256' }` uses SHA-256 for BOTH the OAEP digest and MGF1 — matching the device's
   OAEPParameterSpec("SHA-256", "MGF1", MGF1ParameterSpec.SHA256) in IntercomCrypto.kt.
   (Android's "OAEPWithSHA-256AndMGF1Padding" string would default MGF1 to SHA-1; the
   device avoids the string form for exactly that reason. Do not change either side
   without the other.)

   Both sides are proven against the SAME committed fixture,
   resources/test-vectors/intercom-rsa-oaep.json (repo root) — see
   intercomCrypto.test.js here and IntercomCryptoTest.kt on the device. */

const RSA_OAEP_SHA256 = { name: 'RSA-OAEP', hash: 'SHA-256' };

export function base64ToBytes(b64) {
  const bin = atob(b64);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

export function bytesToBase64(bytes) {
  let s = '';
  for (let i = 0; i < bytes.length; i++) s += String.fromCharCode(bytes[i]);
  return btoa(s);
}

function subtle() {
  const s = typeof crypto !== 'undefined' && crypto.subtle;
  if (!s) {
    // WebCrypto's subtle API only exists in secure contexts (https / localhost).
    throw new Error('WebCrypto unavailable — this page must be served over HTTPS.');
  }
  return s;
}

/**
 * Encrypt a WiFi secret (the password) to the device's per-exchange public key.
 *
 * @param {string} publicKeySpkiBase64  the `publicKey` field from the
 *   `security/generate.encryption.keys` result: base64 of an X.509 SPKI DER RSA-2048 key
 * @param {string} secretUtf8           the password, exactly as typed
 * @returns {Promise<string>} base64 RSA-OAEP ciphertext — the `secret` field of
 *   `wifi/change.credentials`
 */
export async function encryptWifiSecret(publicKeySpkiBase64, secretUtf8) {
  const der = base64ToBytes(publicKeySpkiBase64);
  const key = await subtle().importKey('spki', der, RSA_OAEP_SHA256, false, ['encrypt']);
  const ciphertext = await subtle().encrypt(
    { name: 'RSA-OAEP' },
    key,
    new TextEncoder().encode(secretUtf8),
  );
  return bytesToBase64(new Uint8Array(ciphertext));
}

/* Decrypt exists ONLY for the cross-implementation vector test — the webapp never
   holds a private key in production. Exported so the Jest suite can prove the pinned
   parameters against the shared fixture with the same primitives the page ships. */
export async function decryptForTest(privateKeyPkcs8Base64, ciphertextBase64) {
  const der = base64ToBytes(privateKeyPkcs8Base64);
  const key = await subtle().importKey('pkcs8', der, RSA_OAEP_SHA256, false, ['decrypt']);
  const plaintext = await subtle().decrypt(
    { name: 'RSA-OAEP' },
    key,
    base64ToBytes(ciphertextBase64),
  );
  return new TextDecoder().decode(plaintext);
}
